import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { loginUser, logoutUser, refreshAccessToken } from "./AuthService";
import { jwtDecode } from "jwt-decode";
import { usePopup } from "../context/PopupContext";

const DEBUG_AUTH = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true';
const AuthContext = createContext(null);
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showPopup } = usePopup();
  const refreshIntervalRef = useRef(null);
  const logoutCallbacksRef = useRef([]);
  const ENABLE_INACTIVITY_LOGOUT = false; // Set to true to re-enable inactivity auto-logout
  const INACTIVITY_TIMEOUT_MINUTES = 480;
  const INACTIVITY_TIMEOUT_MS = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
  const LOGOUT_COUNTDOWN_SECONDS = 5;

  // Declare logout early (will be defined below)
  const logoutRef = useRef(null);

  const startRefreshInterval = useCallback(async () => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      if (DEBUG_AUTH) console.warn("Auth: Token refresh scheduling failed - no id token");
      return;
    }
    try {
      const decodedToken = jwtDecode(idToken);
      const expiryTime = decodedToken.exp * 1000;
      const now = Date.now();
      const refreshOffset = 5 * 60 * 1000;
      const timeToRefresh = expiryTime - now - refreshOffset;
      if (timeToRefresh <= 0) {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) startRefreshInterval();
        else logoutRef.current?.("Refresh token failure");
        return;
      }
      if (refreshIntervalRef.current) clearTimeout(refreshIntervalRef.current);
      refreshIntervalRef.current = setTimeout(async () => {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) startRefreshInterval();
        else logoutRef.current?.("Refresh token failure");
      }, timeToRefresh);
    } catch (error) {
      if (DEBUG_AUTH) console.error("Auth: Token validation failed");
      logoutRef.current?.("Invalid access token");
    }
  }, []); // No dependencies - uses ref for logout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success') === 'true' && params.has('id_token')) {
      localStorage.setItem('idToken', params.get('id_token'));
      localStorage.setItem('userLoggedIn', 'true');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  useEffect(() => {
    const checkAndSetUser = async () => {
      setLoading(true);
      let idToken = localStorage.getItem("idToken");
      if (!idToken && process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_LOCAL_LAMBDAS === 'true') {
        const mockTokenPayload = {
          sub: "local-dev-user",
          name: "Local Developer",
          email: "local-dev@example.com",
          iat: Math.floor(Date.now() / 1000),
          exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60)
        };
        const mockJWT = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9." +
                       btoa(JSON.stringify(mockTokenPayload)) +
                       ".mock-signature-for-local-dev";
        localStorage.setItem("idToken", mockJWT);
        setUser({
          email: "local-dev@example.com",
          name: "Local Developer", 
          sub: "local-dev-user",
          role: "developer",
          'custom:membership': 'standard',
          'custom:messagesUsed': 0,
          'custom:limit': 999999,
          'custom:registrationDate': new Date().toISOString().split('T')[0],
          'custom:resetDate': '2025-12-31',
          'custom:nextBillingDate': '2025-12-31',
          'custom:accountStatus': 'active',
          'custom:billingCycle': 'Start of month',
          'custom:billingDay': 1,
          'custom:paymentMethod': 'Credit',
          'custom:trialEndDate': 'NA',
          'custom:lastPaymentDate': 'na',
          'custom:planHistory': 'na'
        });
        setLoading(false);
        return;
      }
      if (!idToken) {
        // No token on THIS origin — but a shared .limkc.com session may already
        // exist (e.g. you signed in on limkc.com, then opened Chatty on
        // app.limkc.com). The csrf_token cookie is JS-readable + Domain=limkc.com,
        // so its presence signals a live session; bootstrap by refreshing from
        // the shared refreshToken cookie, which now persists a fresh idToken.
        if (/(?:^|;\s*)csrf_token=/.test(document.cookie)) {
          try { await refreshAccessToken(); } catch (e) {}
          idToken = localStorage.getItem("idToken");
        }
      }
      if (!idToken) {
        if (user) logout("Missing tokens");
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const decodedUser = jwtDecode(idToken);
        setUser({
          ...decodedUser,
          email: decodedUser.email,
          name: decodedUser.name || decodedUser.email || "Unknown User",
          sub: decodedUser.sub,
          role: decodedUser['custom:role'] || 'user',
          'custom:membership': decodedUser['custom:membership'] || 'standard',
          'custom:messagesUsed': decodedUser['custom:messagesUsed'] || 0,
          'custom:limit': decodedUser['custom:limit'] || 500,
          'custom:voiceUsage_percent': decodedUser['custom:voiceUsage_percent'] || 0,
          'custom:registrationDate': decodedUser['custom:registrationDate'] || new Date().toISOString().split('T')[0],
          'custom:resetDate': decodedUser['custom:resetDate'] || '2025-04-01',
          'custom:nextBillingDate': decodedUser['custom:nextBillingDate'] || '2025-04-01',
          'custom:accountStatus': decodedUser['custom:accountStatus'] || 'active',
          'custom:billingCycle': decodedUser['custom:billingCycle'] || 'Start of month',
          'custom:billingDay': decodedUser['custom:billingDay'] || 1,
          'custom:paymentMethod': decodedUser['custom:paymentMethod'] || 'Credit',
          'custom:trialEndDate': decodedUser['custom:trialEndDate'] || 'NA',
          'custom:lastPaymentDate': decodedUser['custom:lastPaymentDate'] || 'na',
          'custom:planHistory': decodedUser['custom:planHistory'] || 'na'
        });
        setLoading(false);
      } catch (error) {
        if (DEBUG_AUTH) console.error("Auth: Authentication failed");
        logout("Invalid token");
        setLoading(false);
      }
    };
    checkAndSetUser();
    return () => {
      clearInterval(refreshIntervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount - logout handled via ref
  useEffect(() => {
    if (user) startRefreshInterval();
    else if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, [user, startRefreshInterval]);
  useEffect(() => {
    if (!ENABLE_INACTIVITY_LOGOUT) return;
    let inactivityTimeout;
    const startInactivityLogout = () => {
      if (!user) return;
      showPopup(
        `Inactivity detected, logging out in ${LOGOUT_COUNTDOWN_SECONDS} seconds...`,
        "logout",
        { countdown: LOGOUT_COUNTDOWN_SECONDS }
      );
      setTimeout(() => {
        if (user) {
          logoutRef.current?.("Inactivity timeout");
          showPopup("Logged out due to inactivity", "success");
        }
      }, LOGOUT_COUNTDOWN_SECONDS * 1000);
    };
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(() => {
        if (user) {
          if (DEBUG_AUTH) console.warn("Auth: Session timeout initiated");
          startInactivityLogout();
        }
      }, INACTIVITY_TIMEOUT_MS);
    };
    window.addEventListener("mousemove", resetInactivityTimer);
    window.addEventListener("keydown", resetInactivityTimer);
    resetInactivityTimer();
    return () => {
      clearTimeout(inactivityTimeout);
      window.removeEventListener("mousemove", resetInactivityTimer);
      window.removeEventListener("keydown", resetInactivityTimer);
    };
  }, [user, showPopup, INACTIVITY_TIMEOUT_MS, LOGOUT_COUNTDOWN_SECONDS]); // All dependencies included
  const login = async (email, password, turnstileToken) => {
    setLoading(true);
    const response = await loginUser(email, password, turnstileToken);
    setLoading(false);
    if (response.success) {
      setUser(response.user);
    }
    return response.success;
  };
  const registerLogoutCallback = (callback) => {
    logoutCallbacksRef.current.push(callback);
    return () => {
      logoutCallbacksRef.current = logoutCallbacksRef.current.filter(cb => cb !== callback);
    };
  };
  const logout = useCallback(async (reason = "Manual logout") => {
    setLoading(true);
    logoutCallbacksRef.current.forEach(callback => {
      try {
        callback();
      } catch (error) {
        if (DEBUG_AUTH) console.error("Auth: Logout callback failed");
      }
    });
    // Kill the shared session server-side and WAIT for it: /user_logout returns
    // Max-Age=0; Domain=limkc.com Set-Cookie headers that wipe access_token,
    // refreshToken and csrf_token. It is ONE shared cookie, so this signs the
    // user out of app.limkc.com AND the apex at once. Awaiting guarantees the
    // cookie is gone before we navigate — no window where the apex could still
    // read a live session (the security hole if we redirected first).
    try { await logoutUser(); } catch (e) { /* local logout still proceeds */ }
    // Wipe every trace on THIS origin. The apex's localStorage is a DIFFERENT
    // origin and cannot be touched from here; the apex clears its own on next
    // load when it sees the shared cookie is gone (see content-site Base.astro).
    try {
      localStorage.removeItem("idToken");
      localStorage.removeItem("userLoggedIn");
      localStorage.removeItem("logoutCountdownActive");
    } catch (e) {}
    setUser(null);
    setLoading(false);
    // Hard-redirect to the apex home; it self-cleans and shows logged-out.
    window.location.replace("https://limkc.com");
  }, [setLoading, setUser]);

  // Update logout ref whenever logout changes
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);
  const forceLogout = useCallback(() => {
    logout("Token expired");
  }, [logout]);
  useEffect(() => {
    const handleForceLogout = () => {
      forceLogout();
    };
    window.addEventListener('forceLogout', handleForceLogout);
    return () => window.removeEventListener('forceLogout', handleForceLogout);
  }, [forceLogout]);
  return (
    <AuthContext.Provider value={{ user, login, logout, forceLogout, loading, setLoading, setUser, registerLogoutCallback }}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => useContext(AuthContext);
export const EXTENDED_SESSION_CONFIG = {
  INACTIVITY_TIMEOUT_MINUTES: 480, 
  INACTIVITY_TIMEOUT_MS: 480 * 60 * 1000,
  REFRESH_OFFSET_MS: 5 * 60 * 1000 
};